import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { Col, Spinner } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import CarouselComponent from '../Generic/CarouselComponent';
import ProductModal from './ProductModal';
import DeleteButton from '../Generic/DeleteButton';
import {
  deleteProduct,
  fetchProductLogs,
  getProductDetailsDirect,
} from '../../clients/product_client';
import { getUsersForOrder } from '../../clients/user_client';
import SupplierDetailModal from '../Supplier/SupplierDetailModal';
import ManufacturerDetailModal from '../Manufacturer/ManufacturerDetailModal';
import UpdateAmountModal from './UpdateAmountModal';
import './ProductComponentStyle.css';
import '../Glass/ProcureModalStyle.css';
import '../Glass/GlassStyle.css';
import {
  calculatePriceAfterDiscount,
  formatDecimalNumber,
  getCurrencySymbol,
  getExpiryCategoryLabel,
  isKitProduct,
  getEffectiveStock,
  getKitStockDisplay,
} from '../../config_and_helpers/helpers';
import { getProductAliquotsLocation } from '../../utils/productDisplay';
import { groupLotLocations } from '../../utils/storagePlace';
import ProductLotLocations from './ProductLotLocations';
import { resolveOpenedLotCount } from '../../utils/openedLotCount';
import OpenedLotCount from './OpenedLotCount';
import { CURRENCY_SYMBOLS } from '../../config_and_helpers/config';
import SimilarProductsTable from './SimilarProductsTable';
import NavigateToUrlIcon from '../Generic/NavigateToUrlIcon';
import StockItemLogsTable from './StockItemLogsTable';
import StockItemTable from './StockItemTable';
import KitInventoryPanel from './KitInventoryPanel';
import ComponentInventoryModal from './ComponentInventoryModal';
import InventoryRequestOrderPanel from './InventoryRequestOrderPanel';
import ProductOpenProcurementSection from './ProductOpenProcurementSection';
import ProductOpeningStatisticsSection from './ProductOpeningStatisticsSection';
import ProductProtocolsSection from './ProductProtocolsSection';
import ProductEnrichmentPendingBadge from '../ProductEnrichment/ProductEnrichmentPendingBadge';
import { hasOpenProcurement } from '../../utils/productOpenProcurement';
import { logError, logInfo } from '../../config_and_helpers/logger';
import { useLabStorageNodes } from '../Storage/useLabStorageNodes';
import { getStorageNodeTypeLabel, readSlotCount } from '../../utils/storageTree';

const LOG_CONTEXT = 'InventoryModal';

/**
 * Modal for managing product inventory (stock items). Fetches user list for the stock item User /
 * Opened By dropdown (any lot, including order-linked) and passes it to StockItemTable.
 * `enforceFocus` is off so portaled expiry/opened-on calendars remain interactive.
 *
 * @typedef {Object} InventoryModal
 * @property {boolean} isSupplier - Flag indicating if the user is a supplier.
 * @property {Object} product - The product object.
 * @property {function} updateProducts - Function to update the products.
 * @property {function} clearSearchValue - Function to clear the search value.
 * @property {function} fetchProduct - Function to fetch the product.
 * @property {function} handleProductUpdateFromSupplierTable - Function to re-fetch teh suppliers list.
 * @property {boolean} inventoryModalButtonLargeFontSize - Flag indicating if the inventory modal button should have a large font size.
 */
const InventoryModal = ({
  token,
  isSupplier,
  product,
  updateProducts,
  clearSearchValue,
  fetchProduct,
  handleProductUpdateFromSupplierTable,
  inventoryModalButtonLargeFontSize,
  linkChildren,
  hideTrigger = false,
  show: controlledShow,
  onHide: controlledOnHide,
  triggerClassName = '',
  prefetchOnHover = true,
  /**
   * Map place target. Each lot row gets Place here for this shelf, drawer,
   * rack, or box.
   * @type {{ box: Object, lockedSlotIndex: number|null, onPlaced?: Function }|null}
   */
  placeTarget = null,
}) => {
  // State for controlling the visibility of the modal (uncontrolled unless `show` is passed).
  const [internalShow, setInternalShow] = useState(false);
  const isViewer = useIsLabViewer();
  const isControlled = controlledShow !== undefined;
  const show = isControlled ? controlledShow : internalShow;
  const { nodes: storageNodes, ready: storageReady } = useLabStorageNodes(
    token,
    show && !isSupplier
  );
  const storageTree = storageReady ? storageNodes : null;
  const placeSlotCount = readSlotCount(placeTarget?.box);
  const lockedSlot = placeTarget?.lockedSlotIndex;
  const [chosenSlot, setChosenSlot] = useState(
    lockedSlot != null ? String(lockedSlot) : ''
  );
  useEffect(() => {
    setChosenSlot(lockedSlot != null ? String(lockedSlot) : '');
  }, [placeTarget?.box?.id, lockedSlot]);
  const resolvedPlaceSlot = !placeSlotCount
    ? null
    : chosenSlot === ''
      ? null
      : Number(chosenSlot);
  const placeHere = placeTarget
    ? {
        box: placeTarget.box,
        slotIndex: resolvedPlaceSlot,
        slotRequired: Boolean(placeSlotCount),
        onPlaced: placeTarget.onPlaced,
      }
    : null;
  const setShow = next => {
    if (isControlled) {
      if (!next) controlledOnHide?.();
      return;
    }
    setInternalShow(next);
  };

  // State for managing the product items in stock
  const [items, setItems] = useState(product?.items || []);
  // State for managing the showing of a new empty item table row
  const [addNewItem, setAddNewItem] = useState(false);
  // Ref to track if we're in the middle of a deletion operation
  const isDeletingRef = useRef(false);

  // Multi-component state - track which stock items have expanded component views

  // Ref to track if we're in the middle of a stock update operation
  const isUpdatingStockRef = useRef(false);
  // Ref to track if we're in the middle of a stock item operation (create/update/delete)
  const isStockItemOperationRef = useRef(false);
  //Calculate the current stock of subunits of the given product
  const [totalItemStock, setTotalItemStock] = useState(
    (product?.items || []).reduce(
      (total, item) => total + (item?.item_sub_stock || 0),
      0
    )
  );
  const [productLogs, setProductLogs] = useState([]);

  // Bumped when a kit lot is opened or un-marked as a unit. Either way the
  // backend cascades to the kit's component lots, and those live in
  // KitInventoryPanel's own state, which a product refetch does not touch.
  const [kitComponentsRefreshSignal, setKitComponentsRefreshSignal] =
    useState(0);
  const [kitComponentsRefreshReason, setKitComponentsRefreshReason] =
    useState(null);

  // User list for stock item "User" dropdown when item has no order
  const [userSelectList, setUserSelectList] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // Keep the fetched suppliers state for caching fetched data
  // eslint-disable-next-line no-unused-vars
  const [fetchedSuppliers, setFetchedSuppliers] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const fetchedSuppliersRef = useRef(new Set()); // Ref storing the supplier Ids to prevent duplicate requests

  // Ref to track loading state for hover functionality
  const isLoadingRef = useRef(false);
  // Ref to track if product has been fetched to prevent duplicate requests
  const hasFetchedProductRef = useRef(false);

  // Determine if the product has sub-units and is of the relevant unit type,
  // which requires the progress bar UI to track how much sub-units remain
  const showBar =
    product.units_per_sub_unit !== null &&
    (product.unit === 'Box' || product.unit === 'Package');
  const kitStockDisplay = isKitProduct(product)
    ? getKitStockDisplay(product)
    : null;
  // Live lots win once they exist. The initial `[]` before get_details must
  // not count as zero opened lots when the list has no `opened_lot_count`.
  const openedLotSource =
    Array.isArray(product?.items) || items.length > 0
      ? { items, opened_lot_count: product?.opened_lot_count }
      : { opened_lot_count: product?.opened_lot_count };
  const openedLotCount = resolveOpenedLotCount(openedLotSource);
  const lotLocationGroups = groupLotLocations(items, storageTree, product?.location);
  const locationText = String(product?.location || '').trim();

  useEffect(() => {
    if (!product?.id || !locationText) return;
    logInfo(LOG_CONTEXT, 'product location string shown', {
      productId: product.id,
      locationLength: locationText.length,
    });
  }, [product?.id, locationText]);

  // useEffect to update the total item stock upon stock update
  useEffect(() => {
    setTotalItemStock(
      (items || []).reduce(
        (total, item) => total + (item?.item_sub_stock || 0),
        0
      )
    );
  }, [items]);

  // useEffect to update items state when product changes (e.g., when lazy loading fetches full product data)
  useEffect(() => {
    // Don't override items state if we're in the middle of a deletion, stock update, or stock item operation
    if (
      isDeletingRef.current ||
      isUpdatingStockRef.current ||
      isStockItemOperationRef.current
    ) {
      return;
    }

    if (product?.items && Array.isArray(product.items)) {
      // Only update items if they're actually different to avoid unnecessary re-renders
      setItems(prevItems => {
        const itemsAreDifferent =
          JSON.stringify(prevItems) !== JSON.stringify(product.items);

        if (itemsAreDifferent) {
          return product.items;
        } else {
          return prevItems;
        }
      });
    }

    // Clear loading state when product data is loaded
    if (
      product?.items &&
      Array.isArray(product.items) &&
      product.items.length > 0
    ) {
      isLoadingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.items]);

  // Fetch user list when modal opens (User / Opened By picker on every stock item)
  useEffect(() => {
    if (show && token) {
      setIsLoadingUsers(true);
      getUsersForOrder(token)
        .then(users => {
          if (Array.isArray(users) && !users.error) {
            const formatted = users.map(user => ({
              value: user.id,
              label: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
            }));
            setUserSelectList(formatted);
          } else {
            setUserSelectList([]);
          }
        })
        .catch(() => setUserSelectList([]))
        .finally(() => setIsLoadingUsers(false));
    } else if (!show) {
      setUserSelectList([]);
    }
  }, [show, token]);

  /**
   * Apply kit lots from get_details even if a related-part table just set
   * operation skip-refs. Backend is the source of truth after component sync.
   *
   * @param {Object|null|undefined} details
   */
  const applyKitLotsFromServer = useCallback(details => {
    isDeletingRef.current = false;
    isUpdatingStockRef.current = false;
    isStockItemOperationRef.current = false;
    if (Array.isArray(details?.items)) {
      setItems(details.items);
      logInfo(LOG_CONTEXT, 'applyKitLotsFromServer', {
        productId: details?.id ?? product?.id ?? null,
        stock: details?.stock,
        effectiveStock: details?.effective_stock,
        itemCount: details.items.length,
      });
    }
  }, [product?.id]);

  /**
   * Re-read this kit after a component stock/lot change so header count and
   * kit lots match the backend re-sync.
   *
   * @param {{ componentId?: * }} [meta]
   */
  const handleKitComponentStockSync = useCallback(
    async (meta = {}) => {
      if (!token || !product?.id) return;
      logInfo(LOG_CONTEXT, 'kit component-sync start', {
        productId: product.id,
        componentId: meta.componentId ?? null,
      });
      try {
        const details = await getProductDetailsDirect(token, product.id);
        if (details && typeof details === 'object') {
          applyKitLotsFromServer(details);
        } else {
          logError(LOG_CONTEXT, 'kit component-sync missing details', {
            productId: product.id,
          });
        }
      } catch (err) {
        logError(LOG_CONTEXT, 'kit component-sync failed', {
          productId: product.id,
          status: err?.response?.status ?? null,
        });
      }
      if (fetchProduct) fetchProduct({ force: true });
    },
    [applyKitLotsFromServer, fetchProduct, product?.id, token]
  );

  /**
   * Re-read this product's opening logs from the server.
   *
   * `checkItemsForUseStatus` only ever adds a log row (when `in_use` is true),
   * so un-marking a lot leaves a stale opening record behind. The server is the
   * only authority on which logs survive a close, including the ones the
   * backend removes for a kit's component lots.
   */
  const refreshProductLogs = useCallback(() => {
    if (!token || !product?.id) return;
    fetchProductLogs(token, product.id).then(response => {
      if (response && response.success) {
        setProductLogs(response.data);
        logInfo(LOG_CONTEXT, 'product logs refreshed', {
          productId: product.id,
          logCount: Array.isArray(response.data) ? response.data.length : 0,
        });
      }
    });
  }, [token, product?.id]);

  // UseEffect in charge of fetching related product logs, if any
  useEffect(() => {
    if (show) {
      refreshProductLogs();
    }
  }, [show, refreshProductLogs]);

  // Function to filter out a log by it's ID and then reset the productLogs state
  const deleteProductLogFromState = logIdToDelete => {
    const filteredLogs = productLogs.filter(log => log.id !== logIdToDelete);

    setProductLogs(filteredLogs);
  };

  // Function to close the modal.
  const handleClose = () => {
    setShow(false);
    // Reset loading state when modal is closed
    isLoadingRef.current = false;
  };
  // Function to show the modal and fetch product data if needed.
  const handleShow = () => {
    setShow(true);
    // Fetch product data when modal is opened if not already fetched
    if (fetchProduct && !hasFetchedProductRef.current) {
      hasFetchedProductRef.current = true;
      fetchProduct();
    }
  };

  // Function to show a new stock item row in the Stock items table.
  const addStockItem = () => {
    setAddNewItem(!addNewItem);
  };

  const updateProductStock = action => {
    if (action === 'add') {
      setTotalItemStock(prevStockNumber => prevStockNumber + 1);
    } else {
      setTotalItemStock(prevStockNumber => prevStockNumber - 1);
    }
  };

  // Function to update the stock items array
  const updateStockItems = ({
    data, // The data to update the stock items with
    deleteItem = false, // Whether to delete an item from the stock items array
    replaceSingleItem = false, // Whether to replace a single stock item
    replaceItems = false, // Whether to replace the entire stock items array
  }) => {
    // Mark that we're performing a stock item operation
    isStockItemOperationRef.current = true;

    // If 'addNewItem' is true, a new product is being created, so reset 'addNewItem' to false after adding the new stock item
    if (addNewItem) addStockItem();

    // Check if the deleteItem flag is true to remove the specified item by its ID
    if (deleteItem) {
      isDeletingRef.current = true; // Mark that we're deleting
      setItems(prevItems => {
        const filteredItems = prevItems.filter(item => item.id !== data.id);
        // Reset the deletion flag after a longer delay to allow the state update to complete
        setTimeout(() => {
          isDeletingRef.current = false;
          isStockItemOperationRef.current = false;
        }, 500);
        return filteredItems;
      });
      return; // Exit early since we don't need to update items further
    }

    // Check if replaceSingleItem is true to replace a single item in the list
    if (replaceSingleItem) {
      // Keep the flag as true during the update to prevent useEffect from overriding
      setItems(prevItems => {
        const updatedItems = prevItems.map(item =>
          item.id === data.id ? data : item
        );
        // Reset the stock item operation flag after a longer delay to allow the state update to complete
        setTimeout(() => {
          isStockItemOperationRef.current = false;
        }, 1000);
        return updatedItems;
      });
      return; // Exit early since the replacement is complete
    }

    // Handle multiple items if 'data' is an array
    if (Array.isArray(data)) {
      if (replaceItems) {
        // Replace the entire items array with the new array 'data'
        setItems(data);
        // Reset the stock item operation flag after a longer delay
        setTimeout(() => {
          isStockItemOperationRef.current = false;
        }, 500);
      } else {
        // Mark that we're updating stock
        isUpdatingStockRef.current = true;
        // Append multiple new items from the 'data' array to the existing items array
        setItems(prevItems => {
          const newItems = [...prevItems, ...data];
          // Reset the update flag after a longer delay to allow the state update to complete
          setTimeout(() => {
            isUpdatingStockRef.current = false;
            isStockItemOperationRef.current = false;
          }, 500);
          return newItems;
        });
      }
    } else {
      // Add a single new item to the existing items array
      setItems(prevItems => {
        const newItems = [...prevItems, data];
        // Reset the stock item operation flag after a longer delay
        setTimeout(() => {
          isStockItemOperationRef.current = false;
        }, 500);
        return newItems;
      });
    }
  };

  /**
   * Opening history changed on this product's own lots table.
   *
   * Refetches product detail and opening logs. For a kit it also signals
   * `KitInventoryPanel` to reload its related-part lots, because opening a kit
   * lot as a unit opens the kit's component lots server-side (and un-marking it
   * closes them again) — rows that are not part of this product's `get_details`
   * payload.
   *
   * @param {Object} [change] - Detail from `StockItemComponent`
   * @param {boolean} [change.closed] - Lot went from in use to not in use
   * @param {boolean} [change.inUse] - Submitted in-use flag
   * @param {number|string|null} [change.stockItemId]
   */
  const handleLotsOpeningHistoryChange = (change = {}) => {
    if (fetchProduct) fetchProduct({ force: true });

    // A close deletes the opening record, which the optimistic path cannot do.
    refreshProductLogs();

    if (isKitProduct(product)) {
      const reason = change.closed ? 'kit-lot-closed' : 'kit-lot-opened';
      setKitComponentsRefreshSignal(prev => prev + 1);
      setKitComponentsRefreshReason(reason);
      logInfo(LOG_CONTEXT, 'kit lot opening changed, refreshing components', {
        productId: product?.id,
        catNum: product?.cat_num,
        stockItemId: change.stockItemId ?? null,
        inUse: Boolean(change.inUse),
        reason,
      });
    }
  };

  // Function in charge of updating the product logs on the client-side to reflect any changes confirmed
  // to be completed by the server
  const updateProductLogs = data => {
    // Update or add to the productLogs state based on stock_item_id
    setProductLogs(prevLogs => {
      const logIndex = prevLogs.findIndex(log => log.stock_item_id === data.id);

      if (logIndex !== -1) {
        // If the log with this stock_item_id already exists, update it
        const updatedLogs = [...prevLogs];
        updatedLogs[logIndex] = {
          ...updatedLogs[logIndex],
          batch: data.batch,
          expiry: data.expiry,
          person_name: data.opened_by_name,
          date_of_opening: data.opened_on,
          product: data.product, // Or whichever other fields you need to update
          order_id: data.order?.id ?? data.order_id ?? null,
        };
        return updatedLogs;
      } else {
        // If it doesn't exist, add a new log entry
        return [
          ...prevLogs,
          {
            id: data.log_id || new Date().getTime(), // Assuming `log_id` exists; otherwise, generating a temporary ID
            stock_item_id: data.id,
            batch: data.batch,
            expiry: data.expiry,
            person_name: data.opened_by_name,
            date_of_opening: data.opened_on,
            product: data.product,
            order_id: data.order?.id ?? data.order_id ?? null,
          },
        ];
      }
    });
  };

  return (
    <>
      {/* Button to open the modal, displaying the product's name. */}
      {!hideTrigger && (
        <Button
          variant="link"
          className={triggerClassName}
          onClick={handleShow}
          onMouseEnter={() => {
            if (!prefetchOnHover) return;
            if (fetchProduct && !hasFetchedProductRef.current) {
              hasFetchedProductRef.current = true;
              fetchProduct();
            }
          }}
          style={{
            fontSize: inventoryModalButtonLargeFontSize ? '18px' : '14px',
            marginEnd: '10px',
            padding: 0,
          }}
        >
          {linkChildren ?? product.name}
        </Button>
      )}

      {/* The Modal component that appears upon clicking the button. */}
      <Modal
        show={show}
        onHide={handleClose}
        aria-labelledby="product-modal"
        fullscreen={true}
        dialogClassName="procure-modal-glass procure-modal-glass-inventory"
        enforceFocus={false}
      >
        {/* Modal header: product name + Request order grouped on the left. */}
        <Modal.Header
          closeButton
          className="procure-modal__header inventory-modal-header"
        >
          <div className="inventory-modal-header__leading">
            <Modal.Title id="product-modal" className="inventory-modal-header__title">
              {product.name}
              {!isSupplier && product?.id && token && (
                <ProductEnrichmentPendingBadge
                  productId={product.id}
                  token={token}
                />
              )}
            </Modal.Title>
            {!isSupplier && product?.supplier_cat_item !== true && (
              <InventoryRequestOrderPanel product={product} compact />
            )}
          </div>
        </Modal.Header>

        {/* Modal body containing product details. */}
        <Modal.Body className="procure-modal__body">
          {placeHere ? (
            <div className="storage-place-banner mb-3" role="region" aria-label="Place into storage">
              <span className="fw-semibold">
                Place into {placeTarget.box?.name}
                {placeTarget.box
                  ? ` (${getStorageNodeTypeLabel(placeTarget.box)})`
                  : ''}
              </span>
              {placeSlotCount && lockedSlot != null ? (
                <span className="ms-2">Slot {lockedSlot}</span>
              ) : null}
              {placeSlotCount && lockedSlot == null ? (
                <Form.Select
                  size="sm"
                  className="storage-place-banner__slot"
                  aria-label="Slot"
                  value={chosenSlot}
                  onChange={event => setChosenSlot(event.target.value)}
                >
                  <option value="">Choose a slot</option>
                  {Array.from({ length: placeSlotCount }, (_, index) => index + 1).map(slot => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </Form.Select>
              ) : null}
              {!placeSlotCount && placeTarget.box ? (
                <span className="ms-2 text-muted">No slot — lots sit on this place</span>
              ) : null}
            </div>
          ) : null}
          {/* Show loading spinner if data is still being fetched and product doesn't have full data */}
          {isLoadingRef.current &&
          (!product.items || !Array.isArray(product.items)) ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: '400px' }}
            >
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <Container fluid className="inventory-modal-body-container px-2 px-md-3">
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Catalogue Number: </p>
                </Col>
                <Col>
                  <p className="fs-6">{product.cat_num}</p>
                </Col>
              </Row>
              <div className="glass-panel mb-3">
              <div className="inventory-modal-details-split">
                  <div className="inventory-modal-fields-column">
                    <Row md={3}>
                      <Col>
                        <p className="fs-6 fw-bold">
                          {isKitProduct(product)
                            ? 'Complete kits on hand:'
                            : 'Stock:'}
                        </p>
                      </Col>
                      <Col>
                        {isKitProduct(product) ? (
                          <p className="fs-6">
                            {kitStockDisplay.stock === null
                              ? null
                              : kitStockDisplay.stock}
                            {kitStockDisplay.stock !== null && (
                              <span className="text-muted ms-2">
                                (from components)
                              </span>
                            )}
                            {kitStockDisplay.hasOpenKitLeftover && (
                              <span className="d-block text-muted small mt-1">
                                Leftover units are open kit lots that could not
                                be removed (completable from components:{' '}
                                {kitStockDisplay.effectiveStock}).
                                Kit count is not reduced to consume components.
                              </span>
                            )}
                          </p>
                        ) : (
                          <p className="fs-6 mb-0">
                            {getEffectiveStock(product) === null
                              ? null
                              : `${getEffectiveStock(product)} ${
                                  product.unit === 'Package' ||
                                  product.unit === 'Box'
                                    ? `(${totalItemStock})`
                                    : ''
                                }`}
                          </p>
                        )}
                        <OpenedLotCount count={openedLotCount} />
                      </Col>
                    </Row>
                    <Row md={3}>
                      <Col>
                        <p className="fs-6 fw-bold">Category: </p>
                      </Col>
                      <Col>
                        <p className="fs-6">{product.category}</p>
                      </Col>
                    </Row>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Supplier: </p>
                </Col>
                <Col>
                  <SupplierDetailModal
                    fetchedSuppliersRef={fetchedSuppliersRef}
                    fetchedSuppliers={fetchedSuppliers}
                    setFetchedSuppliers={setFetchedSuppliers}
                    supplierObj={product.supplier}
                    supplierId={product.supplier?.id}
                    isSupplier={isSupplier}
                  />
                </Col>
              </Row>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Manufacturer: </p>
                </Col>
                <Col>
                  {product?.manufacturer && (
                    <ManufacturerDetailModal
                      isSupplier={isSupplier}
                      manufacturerObj={product.manufacturer}
                      manufacturerId={product.manufacturer.id}
                    />
                  )}
                </Col>
              </Row>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Unit: </p>
                </Col>
                <Col>
                  <p className="fs-6">
                    {product.unit === 'Assays'
                      ? 'Reactions / Tests / Assays'
                      : product.unit}
                  </p>
                </Col>
              </Row>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Unit Quantity: </p>
                </Col>
                <Col>
                  <p className="fs-6">
                    {product.unit_quantity !== null &&
                    product.unit_quantity !== undefined
                      ? formatDecimalNumber(product.unit_quantity)
                      : ''}
                  </p>
                </Col>
              </Row>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Units Per Sub Unit: </p>
                </Col>
                <Col>
                  <p className="fs-6">{product.units_per_sub_unit}</p>
                </Col>
              </Row>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Storage: </p>
                </Col>
                <Col>
                  <p className="fs-6">{product.storage}</p>
                </Col>
              </Row>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Location: </p>
                </Col>
                <Col>
                  <p className="fs-6">{locationText || '-'}</p>
                </Col>
              </Row>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Aliquots Location: </p>
                </Col>
                <Col>
                  <p className="fs-6">{getProductAliquotsLocation(product) || '-'}</p>
                </Col>
              </Row>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Price: </p>
                </Col>
                <Col>
                  <div className="fs-6">
                    <div>
                      <span>
                        Pre-Discount:{' '}
                        {product.price !== null && product.price !== undefined
                          ? `${formatDecimalNumber(product.price)}${
                              CURRENCY_SYMBOLS[product.currency]
                                ? `${getCurrencySymbol(product.currency)}`
                                : ''
                            }`
                          : ''}
                      </span>
                    </div>
                    {product.discount && (
                      <div>
                        <span>
                          Post-Discount:{' '}
                          {product.price !== null &&
                          product.price !== undefined &&
                          product.discount !== null &&
                          product.discount !== undefined
                            ? `${calculatePriceAfterDiscount(
                                product.price,
                                product.discount
                              )}${
                                CURRENCY_SYMBOLS[product.currency]
                                  ? ` ${getCurrencySymbol(product.currency)}`
                                  : ''
                              } (-${
                                product.discount !== null &&
                                product.discount !== undefined
                                  ? formatDecimalNumber(product.discount)
                                  : ''
                              }%)`
                            : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
              <Row md={3} className={'mt-2'}>
                <Col className="mt-1">
                  <p className="fs-6 fw-bold">Product Profile: </p>
                </Col>
                <Col>
                  {product.url && (
                    <a
                      href={product.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="d-flex align-items-center"
                      style={{ padding: 0 }}
                    >
                      <span className="me-2">Manufacturer Website</span>
                      <NavigateToUrlIcon iconSize={'1.2rem'} />
                    </a>
                  )}
                </Col>
              </Row>
              <Row md={3}>
                <Col className="mt-1">
                  <p className="fs-6 fw-bold">Expiry Relevant: </p>
                </Col>
                {product.expiry_relevant !== '0' &&
                  product.expiry_relevant !== 'false' &&
                  product.expiry_relevant && (
                    <Col>{getExpiryCategoryLabel(product.expiry_relevant)}</Col>
                  )}
              </Row>
              <Row md={3}>
                <Col className="mt-1">
                  <p className="fs-6 fw-bold">Notes: </p>
                </Col>
                <Col md={8} className="bordered-shadow-container">
                  {product.notes !== null && product.notes}
                </Col>
              </Row>
              {!isSupplier && (
                <div className="inventory-modal-locations">
                  <ProductLotLocations groups={lotLocationGroups} />
                </div>
              )}
                  </div>
                  <div className="inventory-modal-actions-column">
                    {!isKitProduct(product) && (
                      <UpdateAmountModal
                        product={{
                          catNum: product.cat_num,
                          productId: product.id,
                          currentStock: product.stock,
                          ...product,
                        }}
                        onSuccessfulUpdate={updateStockItems}
                      />
                    )}
                    <div className="inventory-modal-procurement-under-stock">
                      <ProductOpeningStatisticsSection product={product} />
                    </div>
                    {!isSupplier && (
                      <div className="inventory-modal-procurement-under-stock">
                        <ProductProtocolsSection
                          product={product}
                          token={token}
                          isSupplier={isSupplier}
                        />
                      </div>
                    )}
                    {hasOpenProcurement(product) && (
                      <div className="inventory-modal-procurement-under-stock">
                        <ProductOpenProcurementSection
                          product={product}
                          clearSearchValue={clearSearchValue}
                          isSupplier={isSupplier}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {isKitProduct(product) && (
                <KitInventoryPanel
                  product={product}
                  token={token}
                  isSupplier={isSupplier}
                  userSelectList={userSelectList}
                  isLoadingUsers={isLoadingUsers}
                  onOpeningHistoryChange={() =>
                    fetchProduct && fetchProduct({ force: true })
                  }
                  storageNodes={storageTree}
                  onComponentStockSync={handleKitComponentStockSync}
                  refreshSignal={kitComponentsRefreshSignal}
                  refreshReason={kitComponentsRefreshReason}
                />
              )}

              {/* Multi-Component Status - Only show if product actually supports multi-components */}
              {(() => {
                // Only show if product supports multi-components OR has valid multi_component_status
                const supportsMultiComponents =
                  product.supports_multi_components === true;
                const hasValidMultiComponentStatus =
                  product.multi_component_status &&
                  (product.multi_component_status.total_components > 0 ||
                    (product.multi_component_status.components &&
                      product.multi_component_status.components.length > 0));

                return supportsMultiComponents || hasValidMultiComponentStatus;
              })() && (
                <>
                  <hr />
                  <Row md={3}>
                    <Col>
                      <p className="fs-6 fw-bold">Multi-Component Status: </p>
                    </Col>
                    <Col>
                      <div className="d-flex align-items-center">
                        {(product.multi_component_status?.total_components ||
                          0) > 0 ? (
                          <span className="badge bg-primary">Master Unit</span>
                        ) : product.items &&
                          product.items.some(
                            item => item.is_master_stock_item
                          ) ? (
                          <span className="badge bg-primary">Master Unit</span>
                        ) : product.items &&
                          product.items.some(
                            item => item.is_component_stock_item
                          ) ? (
                          <span className="badge bg-secondary">Sub Unit</span>
                        ) : (
                          <span className="badge bg-secondary">Sub Unit</span>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {/* Multi-Component Details */}
                  {product.multi_component_status?.components &&
                    product.multi_component_status.components.length > 0 && (
                      <Row className="mt-3">
                        <Col>
                          <div className="glass-panel border rounded p-3">
                            <h6 className="mb-3">Component Details:</h6>
                            <div className="row">
                              {product.multi_component_status.components.map(
                                (component, index) => (
                                  <div key={index} className="col-12 mb-2">
                                    <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                                      <div>
                                        <div className="d-flex align-items-center">
                                          <ComponentInventoryModal
                                            token={token}
                                            isSupplier={isSupplier}
                                            componentData={component}
                                            updateProducts={updateProducts}
                                            clearSearchValue={clearSearchValue}
                                            handleProductUpdateFromSupplierTable={
                                              handleProductUpdateFromSupplierTable
                                            }
                                          />
                                          <span className="ms-2 text-muted">
                                            ({component.cat_num})
                                          </span>
                                        </div>
                                        <br />
                                        <small className="text-muted">
                                          Quantity per master unit:{' '}
                                          {component.quantity_per_component} |
                                          Location: {component.location} |
                                          Stock: {component.component_stock}
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    )}
                  <hr />
                </>
              )}

              {/* Bootstrap Carousel component to display product images */}
              <hr />
              <CarouselComponent
                images={product.images}
                imageIdKind="productImageId"
              />
              <hr />

              {/* Bootstrap Table component to display the related items to the product currently in stock */}
              {!isSupplier && (
                <div className="glass-table-wrap mb-3">
                  <StockItemTable
                    product={product}
                    items={items}
                    addNewItem={addNewItem}
                    addStockItem={isViewer ? undefined : addStockItem}
                    updateStockItems={updateStockItems}
                    updateProductStock={updateProductStock}
                    showBar={showBar}
                    setTotalItemStock={setTotalItemStock}
                    updateProductLogs={updateProductLogs}
                    userSelectList={userSelectList}
                    isLoadingUsers={isLoadingUsers}
                    useGlassTable
                    headerLabel={
                      isKitProduct(product)
                        ? 'Kit lots (open as a unit)'
                        : 'Stock Items'
                    }
                    onOpeningHistoryChange={handleLotsOpeningHistoryChange}
                    storageNodes={storageTree}
                    placeHere={isViewer ? null : placeHere}
                  />
                </div>
              )}

              {!isSupplier &&
                product?.competing_products &&
                product.competing_products.length > 0 && (
                  <div className="glass-table-wrap mb-3">
                    <SimilarProductsTable
                      similarProducts={product.competing_products}
                      baseProduct={product}
                    />
                  </div>
                )}

              {productLogs.length > 0 && (
                <div className="glass-table-wrap mb-3">
                  <StockItemLogsTable
                    stockItemLogs={productLogs}
                    onSuccessfulDelete={deleteProductLogFromState}
                  />
                </div>
              )}
            </Container>
          )}
        </Modal.Body>
        {/* Modal footer with action buttons. */}
        <Modal.Footer className="procure-modal__footer d-flex flex-row justify-content-between">
          {!isSupplier && !isViewer && (
            <div className="d-flex flex-row">
              <div className="me-2">
                <ProductModal
                  productObj={product}
                  clearSearchValue={clearSearchValue}
                  onSuccessfulSubmit={() => {
                    if (isKitProduct(product)) {
                      handleKitComponentStockSync();
                    } else if (fetchProduct) {
                      fetchProduct({ force: true });
                    }
                  }}
                  handleProductUpdateFromSupplierTable={
                    handleProductUpdateFromSupplierTable
                  }
                />
              </div>
              <DeleteButton
                objectType="product"
                objectName={product.name}
                objectId={product.id}
                deleteFetchFunc={deleteProduct}
                onSuccessfulDelete={updateProducts}
                clearSearchValue={clearSearchValue}
              />
            </div>
          )}
          <Button
            variant="secondary"
            className="procure-modal__close-btn"
            onClick={handleClose}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default InventoryModal;
